import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack } from '@mui/material';
import {
  blockSubscriptionPayment,
  cancelSubscription,
  fetchCustomers,
  fetchPlans,
  fetchSubscriptions,
  fetchTenants,
  unblockSubscriptionPayment,
} from '../api/authApi';
import { getTenantModuleSubscriptions } from '../api/pricingApi';
import { colorTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { DataTable } from '../components/DataTable';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { SubscriptionChangeDialog } from '../components/SubscriptionChangeDialog';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { safeApiErrorMessage } from '../utils/safeApiErrorMessage';
import { getSubscriptionStatusLabel } from '../utils/statusLabels';

const { semantic } = colorTokens;

const STATUSES = ['trial', 'active', 'past_due', 'payment_blocked', 'suspended', 'canceled', 'expired'];

const rowActionSx = {
  minHeight: 32,
  px: 1,
};

const dangerOutlineSx = {
  ...rowActionSx,
  color: semantic.error,
  border: `1px solid ${semantic.error}`,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: 'rgba(211, 47, 47, 0.08)',
    borderColor: semantic.error,
    color: semantic.error,
  },
};

function subscriptionStatusTone(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'active') return 'Success';
  if (normalized === 'trial' || normalized === 'trialing') return 'Info';
  if (normalized === 'past_due') return 'Warning';
  if (normalized === 'expired') return 'Neutral';
  if (normalized === 'canceled' || normalized === 'cancelled') return 'Neutral';
  if (normalized === 'payment_blocked' || normalized === 'blocked' || normalized === 'suspended') {
    return 'Danger';
  }
  return 'Neutral';
}

function paymentFlowTone(status) {
  const normalized = String(status || '').toLowerCase();
  if (!normalized || normalized === 'none' || normalized === 'missing' || normalized === '—') {
    return 'Neutral';
  }
  if (normalized === 'ok' || normalized === 'active' || normalized === 'healthy' || normalized === 'paid') {
    return 'Success';
  }
  if (
    normalized === 'pending'
    || normalized === 'waiting'
    || normalized === 'waiting_manual_confirmation'
    || normalized.includes('pending')
    || normalized.includes('wait')
  ) {
    return 'Warning';
  }
  if (
    normalized === 'blocked'
    || normalized === 'failed'
    || normalized === 'error'
    || normalized === 'payment_blocked'
    || normalized.includes('block')
    || normalized.includes('fail')
  ) {
    return 'Danger';
  }
  return 'Neutral';
}

export function SubscriptionsPage() {
  const { t } = useTranslation(['billing', 'common', 'statuses', 'subscriptionChanges']);
  const { session } = useAuth();
  const canManageChanges = hasPermission(session, 'system.billing.subscription_changes.manage');
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [planId, setPlanId] = useState('');
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [changeSubscription, setChangeSubscription] = useState(null);
  const [changeModules, setChangeModules] = useState([]);

  const emptyValue = t('common:empty_value');
  const recordCount = items.length;

  const formatDate = useCallback((value) => (
    value ? new Date(value).toLocaleString() : emptyValue
  ), [emptyValue]);

  const loadSubscriptions = useCallback(() => {
    setLoading(true);
    setError(null);

    const params = {};
    if (status) params.status = status;
    if (customerId) params.customer_id = customerId;
    if (tenantId) params.tenant_id = tenantId;
    if (planId) params.plan_id = planId;

    return fetchSubscriptions(params)
      .then((r) => setItems(r.data || []))
      .catch(() => setError(t('billing:load_subscriptions_failed')))
      .finally(() => setLoading(false));
  }, [status, customerId, tenantId, planId, t]);

  useEffect(() => {
    fetchCustomers({ per_page: 100 }).then((r) => setCustomers(r.data || [])).catch(() => setCustomers([]));
    fetchTenants({ per_page: 100 }).then((r) => setTenants(r.data || [])).catch(() => setTenants([]));
    fetchPlans({ per_page: 100 }).then((r) => setPlans(r.data || [])).catch(() => setPlans([]));
  }, []);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  const reload = useCallback(() => {
    const params = {};
    if (status) params.status = status;
    if (customerId) params.customer_id = customerId;
    if (tenantId) params.tenant_id = tenantId;
    if (planId) params.plan_id = planId;
    return fetchSubscriptions(params).then((r) => setItems(r.data || []));
  }, [status, customerId, tenantId, planId]);

  const openChangeDialog = useCallback(async (sub) => {
    setChangeSubscription(sub);
    try {
      const modules = sub.tenant_id
        ? await getTenantModuleSubscriptions(sub.tenant_id)
        : [];
      const codes = (Array.isArray(modules) ? modules : modules?.data || [])
        .filter((row) => row.enabled)
        .map((row) => row.module_code);
      setChangeModules(codes);
    } catch {
      setChangeModules([]);
    }
    setChangeDialogOpen(true);
  }, []);

  const handleBlockPayment = useCallback(async (subscriptionId) => {
    setError(null);
    try {
      await blockSubscriptionPayment(subscriptionId, { payment_block_reason: 'manual' });
      await reload();
    } catch (err) {
      setError(safeApiErrorMessage(err, t('billing:block_payment_failed')));
    }
  }, [reload, t]);

  const handleUnblockPayment = useCallback(async (subscriptionId) => {
    setError(null);
    try {
      await unblockSubscriptionPayment(subscriptionId);
      await reload();
    } catch (err) {
      setError(safeApiErrorMessage(err, t('billing:unblock_payment_failed')));
    }
  }, [reload, t]);

  const handleCancelSubscription = useCallback(async (subscriptionId) => {
    setError(null);
    try {
      await cancelSubscription(subscriptionId);
      await reload();
    } catch (err) {
      setError(safeApiErrorMessage(err, t('billing:cancel_subscription_failed')));
    }
  }, [reload, t]);

  const columns = useMemo(
    () => [
      {
        id: 'customer',
        label: t('billing:customer'),
        render: (row) => row.customer?.name || row.customer_id || emptyValue,
      },
      {
        id: 'tenant',
        label: t('billing:tenant'),
        render: (row) => row.tenant?.name || emptyValue,
      },
      {
        id: 'plan',
        label: t('billing:plan'),
        render: (row) => row.plan?.name || row.plan_id || emptyValue,
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => (
          row.status
            ? (
              <StatusBadge
                status={row.status}
                category="subscription"
                size="Sm"
                tone={subscriptionStatusTone(row.status)}
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'source',
        label: t('billing:source'),
        render: (row) => row.source || emptyValue,
      },
      {
        id: 'trial_ends_at',
        label: t('billing:trial_ends'),
        render: (row) => formatDate(row.trial_ends_at),
      },
      {
        id: 'paid_until',
        label: t('billing:paid_until'),
        render: (row) => row.paid_until || emptyValue,
      },
      {
        id: 'grace_until',
        label: t('billing:grace_until'),
        render: (row) => row.grace_until || emptyValue,
      },
      {
        id: 'payment_flow_status',
        label: t('billing:payment_flow_status'),
        render: (row) => {
          if (!row.payment_flow_status) {
            return emptyValue;
          }

          return (
            <StatusBadge
              status={row.payment_flow_status}
              category="generic"
              apiLabel={row.payment_flow_status}
              size="Sm"
              tone={paymentFlowTone(row.payment_flow_status)}
            />
          );
        },
      },
      {
        id: 'current_period_end',
        label: t('billing:period_end'),
        render: (row) => formatDate(row.current_period_end),
      },
      {
        id: 'access',
        label: t('billing:access'),
        render: (row) => (
          <StatusBadge
            status={row.accessible ? 'yes' : 'no'}
            category="generic"
            apiLabel={row.accessible ? t('common:yes') : t('common:no')}
            size="Sm"
            tone={row.accessible ? 'Success' : (row.status === 'payment_blocked' ? 'Danger' : 'Neutral')}
          />
        ),
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        render: (row) => (
          <Stack
            direction="column"
            spacing={0.75}
            useFlexGap
            alignItems="flex-end"
            sx={{
              gap: '6px',
              whiteSpace: 'nowrap',
            }}
          >
            {row.status === 'payment_blocked' ? (
              <Button
                variant="Secondary"
                onClick={() => handleUnblockPayment(row.id)}
                sx={rowActionSx}
              >
                {t('billing:unblock')}
              </Button>
            ) : (
              <Button
                variant="Ghost"
                onClick={() => handleBlockPayment(row.id)}
                sx={dangerOutlineSx}
              >
                {t('billing:block')}
              </Button>
            )}
            <Button
              variant="Ghost"
              onClick={() => handleCancelSubscription(row.id)}
              sx={dangerOutlineSx}
            >
              {t('billing:cancel')}
            </Button>
            {canManageChanges && (
              <Button
                variant="Secondary"
                onClick={() => openChangeDialog(row)}
                sx={rowActionSx}
              >
                {t('subscriptionChanges:changePackage')}
              </Button>
            )}
          </Stack>
        ),
      },
    ],
    [
      canManageChanges,
      emptyValue,
      formatDate,
      handleBlockPayment,
      handleCancelSubscription,
      handleUnblockPayment,
      openChangeDialog,
      t,
    ],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('billing:subscriptions_title')}
      />

      <FilterBar type="Simple">
        <FilterSelect
          label={t('common:status')}
          hideLabel
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {STATUSES.map((subscriptionStatus) => (
            <FilterSelectOption key={subscriptionStatus} value={subscriptionStatus}>
              {getSubscriptionStatusLabel(subscriptionStatus)}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('billing:customer')}
          hideLabel
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          minWidth={180}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {customers.map((customer) => (
            <FilterSelectOption key={customer.id} value={String(customer.id)}>
              {customer.name}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('billing:tenant')}
          hideLabel
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          minWidth={180}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {tenants.map((tenant) => (
            <FilterSelectOption key={tenant.id} value={String(tenant.id)}>
              {tenant.name}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('billing:plan')}
          hideLabel
          value={planId}
          onChange={(e) => setPlanId(e.target.value)}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {plans.map((plan) => (
            <FilterSelectOption key={plan.id} value={String(plan.id)}>
              {plan.name}
            </FilterSelectOption>
          ))}
        </FilterSelect>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('billing:subscriptions_title')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('billing:subscriptions_toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={items}
            getRowKey={(row) => row.id}
            emptyMessage={t('billing:no_subscriptions')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>

      <SubscriptionChangeDialog
        open={changeDialogOpen}
        onClose={() => setChangeDialogOpen(false)}
        subscription={changeSubscription}
        initialModules={changeModules}
        initialUsersCount={changeSubscription?.users_count || 10}
        initialBillingInterval={changeSubscription?.billing_interval || 'monthly'}
        onSuccess={reload}
      />
    </Box>
  );
}
